import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { RON } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function Cart() {
  useEffect(() => window.scrollTo(0, 0), []);
  const { items, updateQty, remove, total } = useCart();

  return (
    <div className="pt-20">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
        <Reveal>
          <p className="overline text-roast">Coșul tău</p>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight mt-3 mb-12">Coș de cumpărături</h1>
        </Reveal>

        {items.length === 0 ? (
          <div className="py-24 flex flex-col items-center gap-6 text-center">
            <ShoppingBag size={48} className="text-sand" />
            <p className="text-muted-foreground text-lg">Coșul tău este gol.</p>
            <Link to="/produse" data-testid="cart-page-shop" className="bg-ink text-cream px-8 py-4 hover:bg-roast transition-colors text-sm font-semibold">
              Descoperă cafeaua
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_380px] gap-16">
            <div className="divide-y divide-line">
              {items.map((i) => (
                <div key={i.key} className="flex gap-5 py-6" data-testid={`cart-page-item-${i.product_id}`}>
                  <img src={i.image} alt={i.name} className="h-32 w-28 object-cover bg-stone" />
                  <div className="flex-1">
                    <p className="overline text-roast">{i.brand}</p>
                    <h3 className="font-display text-2xl">{i.name}</h3>
                    <p className="text-sm text-muted-foreground">{i.grammage}</p>
                    <div className="flex items-center gap-6 mt-4">
                      <div className="flex items-center border border-line">
                        <button onClick={() => updateQty(i.key, i.quantity - 1)} className="p-2" aria-label="minus"><Minus size={14} /></button>
                        <span className="w-10 text-center text-sm">{i.quantity}</span>
                        <button onClick={() => updateQty(i.key, i.quantity + 1)} className="p-2" aria-label="plus"><Plus size={14} /></button>
                      </div>
                      <button onClick={() => remove(i.key)} className="text-muted-foreground hover:text-roast flex items-center gap-1 text-sm">
                        <Trash2 size={15} /> Șterge
                      </button>
                    </div>
                  </div>
                  <div className="font-medium text-lg">{RON(i.price * i.quantity)}</div>
                </div>
              ))}
            </div>

            <div className="lg:sticky lg:top-28 h-fit bg-stone p-8">
              <h2 className="font-display text-3xl mb-6">Sumar comandă</h2>
              <div className="space-y-3 text-sm">
                <Row label="Subtotal" value={RON(total)} />
                <Row label="Livrare" value="Calculată la checkout" />
              </div>
              <div className="flex items-center justify-between border-t border-line mt-6 pt-6">
                <span className="overline">Total</span>
                <span className="font-display text-3xl">{RON(total)}</span>
              </div>
              <Link
                to="/finalizare"
                data-testid="proceed-checkout"
                className="group mt-8 flex items-center justify-center gap-3 bg-ink text-cream py-4 hover:bg-roast transition-colors text-sm font-semibold tracking-wide"
              >
                Finalizează comanda
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-muted-foreground">
    <span>{label}</span>
    <span className="text-ink">{value}</span>
  </div>
);
